// ... existing code ...
import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

function LineChars({ text }: { text: string }) {
  return (
    <>
      {text.split('').map((char, i) => (
        <span key={i} className="label-char inline-block">
          {char === ' ' ? '\u00A0' : char}
        </span>
      ))}
    </>
  );
}

export default function StatsMarquee() {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ctx = gsap.context(() => {
      // Main block reveal
      gsap.fromTo(
        el.querySelectorAll('.about-reveal'),
        { y: 40, opacity: 0, filter: 'blur(12px)' },
        {
          y: 0,
          opacity: 1,
          filter: 'blur(0px)',
          duration: 1.4,
          stagger: 0.15,
          ease: 'power3.out',
          scrollTrigger: { trigger: el, start: 'top 82%' },
        }
      );
      // Character-by-character label reveal
      const labelBlocks = el.querySelectorAll('.label-block');
      labelBlocks.forEach((block, blockIdx) => {
        const chars = block.querySelectorAll('.label-char');
        gsap.fromTo(
          chars,
          { y: 18, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 0.55,
            stagger: 0.035,
            ease: 'power3.out',
            scrollTrigger: { trigger: el, start: 'top 82%' },
            delay: 0.4 + blockIdx * 0.12,
          }
        );
      });
    }, el);
    return () => ctx.revert();
  }, []);

  return (
    <section ref={ref} id="who-we-are" className="relative border-y border-white/5 overflow-hidden scroll-mt-24">
      {/* Subtle fixed background */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Extremely%20subtle%20dark%20abstract%20background%20with%20faint%20geometric%20line%20grid%20texture%20barely%20visible%20on%20near-black%20surface%2C%20muted%20cool%20grey%20and%20desaturated%20violet%20micro-tones%2C%20ultra-minimal%20corporate%20aesthetic%2C%20almost%20invisible%20texture%20like%20fine%20brushed%20metal%20grain%2C%20no%20strong%20colors%20no%20bright%20highlights%2C%20extremely%20low%20contrast%20ambient%20darkness&width=1600&height=900&seq=liberty-about-bg-v4&orientation=landscape"
          alt=""
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-background-100/85" />
      </div>

      <div className="relative max-w-6xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
        <div className="divide-y divide-white/[0.06]">
          {/* Who We Are */}
          <div className="about-reveal py-10 md:py-14 first:pt-0">
            <div className="flex flex-col md:flex-row md:items-start gap-3 md:gap-12 lg:gap-20">
              <div className="md:w-36 lg:w-44 shrink-0 pt-1">
                <div className="label-block inline-block text-sm md:text-base font-mono uppercase tracking-[0.25em] text-primary-400 glow-text">
                  <span className="block">
                    <LineChars text="Who" />
                  </span>
                  <span className="block text-primary-400/60">
                    <LineChars text="We Are" />
                  </span>
                </div>
              </div>
              <p className="flex-1 font-display text-base md:text-lg lg:text-xl leading-[1.5] text-white/70 max-w-2xl">
                Liberty Soft Pte. Ltd. holds and deploys capital across global markets, from its base in Singapore. Our focus is technology and software, the companies writing the systems others will run on. We look wherever opportunity lies and commit only on sound reasoning.
              </p>
            </div>
          </div>

          {/* What Drives Us */}
          <div className="about-reveal py-10 md:py-14">
            <div className="flex flex-col md:flex-row md:items-start gap-3 md:gap-12 lg:gap-20">
              <div className="md:w-36 lg:w-44 shrink-0 pt-1">
                <div className="label-block inline-block text-sm md:text-base font-mono uppercase tracking-[0.25em] text-primary-400 glow-text">
                  <span className="block">
                    <LineChars text="What" />
                  </span>
                  <span className="block text-primary-400/60">
                    <LineChars text="Drives Us" />
                  </span>
                </div>
              </div>
              <p className="flex-1 font-display text-base md:text-lg lg:text-xl leading-[1.5] text-white/85 max-w-xl">
                To back the builders of technology and to hold what we back for the long term.
              </p>
            </div>
          </div>

          {/* Where We're Headed */}
          <div className="about-reveal py-10 md:py-14 last:pb-0">
            <div className="flex flex-col md:flex-row md:items-start gap-3 md:gap-12 lg:gap-20">
              <div className="md:w-36 lg:w-44 shrink-0 pt-1">
                <div className="label-block inline-block text-sm md:text-base font-mono uppercase tracking-[0.25em] text-primary-400 glow-text">
                  <span className="block">
                    <LineChars text="Where" />
                  </span>
                  <span className="block text-primary-400/60">
                    <LineChars text="We're Headed" />
                  </span>
                </div>
              </div>
              <p className="flex-1 font-display text-base md:text-lg lg:text-xl leading-[1.5] text-white/85 max-w-xl">
                To put capital behind software that moves the world forward.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}